import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getAuthInstance } from './client.js';
import type { TakiClaims, UseUserResult } from './types.js';

const EMPTY_CLAIMS: TakiClaims = {};

/**
 * Extrait les claims MyTaki (orgId, orgRole) depuis le token d'un utilisateur.
 * `getIdTokenResult()` déclenche un rafraîchissement si nécessaire et expose `claims`.
 */
async function readClaims(user: User): Promise<TakiClaims> {
  const tokenResult = await user.getIdTokenResult();
  const raw = tokenResult.claims;
  const orgId = typeof raw.orgId === 'string' ? raw.orgId : undefined;
  const orgRole = typeof raw.orgRole === 'string' ? raw.orgRole : undefined;
  return { orgId, orgRole };
}

/**
 * Hook React donnant l'utilisateur courant, ses claims (orgId, orgRole) et l'état de chargement.
 *
 * - S'abonne à `onAuthStateChanged` pour suivre les connexions/déconnexions.
 * - À chaque utilisateur connecté, lit `getIdTokenResult().claims` pour exposer orgId/orgRole.
 * - `loading` reste `true` jusqu'à résolution du premier état d'auth (et de ses claims).
 *
 * Nécessite que `initAuth(app)` ait été appelé au préalable. Si ce n'est pas le cas,
 * le hook reste en état déconnecté (`user: null`, `loading: false`).
 */
export function useUser(): UseUserResult {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<TakiClaims>(EMPTY_CLAIMS);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const auth = getAuthInstance();
    if (!auth) {
      // initAuth pas encore appelé : état déconnecté stable.
      setUser(null);
      setClaims(EMPTY_CLAIMS);
      setLoading(false);
      return;
    }

    let active = true;

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (!active) return;

      if (!nextUser) {
        setUser(null);
        setClaims(EMPTY_CLAIMS);
        setLoading(false);
        return;
      }

      setUser(nextUser);
      setLoading(true);

      void readClaims(nextUser)
        .then((nextClaims) => {
          if (!active) return;
          setClaims(nextClaims);
        })
        .catch(() => {
          if (!active) return;
          setClaims(EMPTY_CLAIMS);
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return { user, claims, loading };
}
